import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND_URL}/api`;

const AnalyticsPage = () => {
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [campaignAnalytics, setCampaignAnalytics] = useState([]);
  const [monthlyServerCost, setMonthlyServerCost] = useState(0);
  const [savingCost, setSavingCost] = useState(false);

  useEffect(() => {
    loadMonthlyServerCost();
  }, []);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadAnalytics(),
          loadCampaignAnalytics()
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [selectedMonth, selectedYear]);

  const loadMonthlyServerCost = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setMonthlyServerCost(response.data.monthly_server_cost || 0);
    } catch (error) {
      console.error('Error loading monthly server cost:', error);
    }
  };

  const saveMonthlyServerCost = async () => {
    setSavingCost(true);
    try {
      await axios.put(`${API}/settings`, {
        monthly_server_cost: monthlyServerCost
      });
      toast.success('Coût mensuel serveur enregistré avec succès');
      // Reload analytics to reflect new cost
      loadAnalytics();
      loadCampaignAnalytics();
    } catch (error) {
      console.error('Error saving monthly server cost:', error);
      toast.error('Erreur lors de l\'enregistrement du coût mensuel serveur');
    } finally {
      setSavingCost(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/detailed`, {
        params: {
          month: selectedMonth,
          year: selectedYear
        }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Erreur lors du chargement des analytics');
      setAnalytics({
        total_calls: 0,
        successful_calls: 0,
        conversion_rate: 0,
        total_minutes: 0,
        cost_per_lead: 0,
        roi: 0,
        sentiment_score: 0,
        average_call_duration: 0,
        leads_generated: 0,
        appointments_set: 0,
        monthly_revenue: 0
      });
    }
  };

  const loadCampaignAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/analytics`, {
        params: {
          month: selectedMonth,
          year: selectedYear
        }
      });
      setCampaignAnalytics(response.data);
    } catch (error) {
      console.error('Error loading campaign analytics:', error);
      toast.error('Erreur lors du chargement des analytics de campagne');
      setCampaignAnalytics([]);
    }
  };

  const toNumber = (value) => {
    if (typeof value === 'number') return value;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const derivedMetrics = useMemo(() => {
    if (!campaignAnalytics || campaignAnalytics.length === 0) {
      return {
        averageConversionRate: 0,
        roiValue: 0,
        totalLeads: 0,
        totalCost: 0,
        totalSuccessfulCalls: 0,
        costPerLeadValue: null
      };
    }

    let totalLeads = 0;
    let totalCost = 0;
    let totalSuccessfulCalls = 0;

    campaignAnalytics.forEach((campaign) => {
      const leads = toNumber(campaign.confirmed_calls ?? 0);
      const successfulCalls = toNumber(campaign.successful_calls ?? 0);
      // Use total_cost if available, otherwise fall back to cost
      const cost = toNumber(campaign.total_cost ?? campaign.cost ?? 0);

      totalLeads += leads;
      totalSuccessfulCalls += successfulCalls;
      totalCost += cost;
    });

    // Conversion Rate: (sum of all confirmed_calls / sum of successful_calls) * 100
    const averageConversionRate = totalSuccessfulCalls > 0
      ? (totalLeads / totalSuccessfulCalls) * 100
      : 0;

    // ROI: (sum of all confirmed_calls / sum of total_cost) * 100
    const roiValue = totalCost > 0
      ? (totalLeads / totalCost) * 100
      : 0;

    // Cost per Lead: sum of total_cost / sum of confirmed_calls
    const costPerLeadValue = totalLeads > 0 ? totalCost / totalLeads : null;

    return {
      averageConversionRate,
      roiValue,
      totalLeads,
      totalCost,
      totalSuccessfulCalls,
      costPerLeadValue
    };
  }, [campaignAnalytics]);

  const conversionRateDisplay = derivedMetrics.averageConversionRate.toFixed(2);
  const roiDisplay = derivedMetrics.roiValue.toFixed(2);
  const costPerLeadDisplay = derivedMetrics.costPerLeadValue === null
    ? 'N/A'
    : `€${derivedMetrics.costPerLeadValue.toFixed(2)}`;

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const years = Array.from({length: 5}, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Analytics Avancées</h1>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2 bg-white rounded-lg border border-gray-300 px-3 py-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Coût mensuel serveur:
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={monthlyServerCost}
                  onChange={(e) => setMonthlyServerCost(parseFloat(e.target.value) || 0)}
                  className="w-24 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="0.00"
                />
                <span className="text-sm text-gray-600">€</span>
                <button
                  onClick={saveMonthlyServerCost}
                  disabled={savingCost}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingCost ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {months.map((month, index) => (
                  <option key={index} value={index}>{month}</option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Appels Total</p>
                <p className="text-3xl font-bold text-gray-900">{analytics.total_calls || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <span className="text-2xl">📞</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Taux de Conversion</p>
                <p className="text-3xl font-bold text-gray-900">{conversionRateDisplay}%</p>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <span className="text-2xl">📈</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">ROI</p>
                <p className="text-3xl font-bold text-gray-900">{roiDisplay}%</p>
              </div>
              <div className="p-3 rounded-full bg-purple-100">
                <span className="text-2xl">💰</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Coût par Lead</p>
                <p className="text-3xl font-bold text-gray-900">{costPerLeadDisplay}</p>
              </div>
              <div className="p-3 rounded-full bg-orange-100">
                <span className="text-2xl">🎯</span>
              </div>
            </div>
          </div>
        </div>

        {/* Campaign Analytics Table */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Performance par Campagne</h2>
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campagne</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appels</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Succès</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taux</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coût</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix par Lead</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ROI</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaignAnalytics.map((campaign) => (
                  <tr key={campaign.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.calls_made}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.successful_calls}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.conversion_rate}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.total_minutes ? campaign.total_minutes.toFixed(2) + ' min' : '0 min'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      €{campaign.cost}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.confirmed_calls > 0 
                        ? `€${((campaign.total_cost || campaign.cost) / campaign.confirmed_calls).toFixed(2)}`
                        : 'N/A'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        campaign.roi > 100 ? 'bg-green-100 text-green-800' : 
                        campaign.roi > 50 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'
                      }`}>
                        {campaign.roi}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;